import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Search, Star, SlidersHorizontal, X } from "lucide-react";
import { playTap } from "@/lib/sounds";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { MemoryCard } from "@/components/MemoryCard";

export default function Memories() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [items, setItems] = useState(null);
  const [cats, setCats] = useState([]);
  const [historyDays, setHistoryDays] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const category = params.get("categoria") || "";
  const favorite = params.get("favoritos") === "1";
  const archived = params.get("arquivadas") === "1";
  const dateFrom = params.get("de") || "";
  const dateTo = params.get("ate") || "";

  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    v ? p.set(k, v) : p.delete(k);
    setParams(p, { replace: true });
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/memories", { params: { q: q || undefined, category: category || undefined, favorite: favorite || undefined, archived, date_from: dateFrom || undefined, date_to: dateTo || undefined, limit: 200 } });
      setItems(data.items);
      setHistoryDays(data.history_days);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, [q, category, favorite, archived, dateFrom, dateTo]);

  useEffect(() => { const t = setTimeout(load, q ? 350 : 0); return () => clearTimeout(t); }, [load, q]);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);

  const patch = async (m, body, msg) => { await api.put(`/memories/${m.id}`, body); if (msg) toast.success(msg); load(); };
  const remove = async (m) => { await api.delete(`/memories/${m.id}`); toast.success("Memória excluída."); load(); };

  return (
    <div className="ga-fade-up px-5 pt-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">Memórias</h1>
        <Link to="/perguntar" className="text-xs font-semibold" style={{ color: "#13C9A3" }} data-testid="memories-ask-link">Perguntar</Link>
      </div>

      <div className="mt-4 flex gap-2">
        <div className="relative flex-1">
          <Search size={18} className="ga-muted absolute left-4 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar: “coisas emprestadas”, “carro”..." className="ga-input pl-11" aria-label="Buscar memórias" data-testid="memories-search-input" />
          {q && <button onClick={() => setQ("")} className="ga-muted absolute right-3 top-1/2 -translate-y-1/2" aria-label="Limpar busca" data-testid="memories-search-clear"><X size={16} /></button>}
        </div>
        <button onClick={() => setShowFilters(!showFilters)} className={`ga-surface flex h-[52px] w-[52px] items-center justify-center rounded-2xl ${showFilters ? "ring-2 ring-[#13C9A3]" : ""}`} aria-label="Filtros" data-testid="memories-filter-toggle">
          <SlidersHorizontal size={18} />
        </button>
      </div>

      {showFilters && (
        <div className="ga-card mt-3 grid grid-cols-2 gap-3 p-3 text-xs" data-testid="memories-filters-panel">
          <label>De<input type="date" value={dateFrom} onChange={(e) => setParam("de", e.target.value)} className="ga-input mt-1 py-2" data-testid="memories-filter-from" /></label>
          <label>Até<input type="date" value={dateTo} onChange={(e) => setParam("ate", e.target.value)} className="ga-input mt-1 py-2" data-testid="memories-filter-to" /></label>
          <label className="col-span-2 flex items-center gap-2"><input type="checkbox" checked={archived} onChange={(e) => setParam("arquivadas", e.target.checked ? "1" : "")} className="accent-[#13C9A3]" data-testid="memories-filter-archived" /> Mostrar arquivadas</label>
        </div>
      )}

      <div className="no-scrollbar -mx-5 mt-4 flex gap-2 overflow-x-auto px-5 pb-1">
        <button onClick={() => { playTap(); setParam("categoria", ""); setParam("favoritos", ""); }} className={`ga-chip ${!category && !favorite ? "ga-chip-active" : ""}`} data-testid="filter-chip-todas">Todas</button>
        <button onClick={() => { playTap(); setParam("favoritos", favorite ? "" : "1"); }} className={`ga-chip ${favorite ? "ga-chip-active" : ""}`} data-testid="filter-chip-favoritos"><Star size={12} /> Favoritos</button>
        {cats.filter((c) => c.count > 0 || !c.is_default).map((c) => (
          <button key={c.name} onClick={() => { playTap(); setParam("categoria", category === c.name ? "" : c.name); }} className={`ga-chip ${category === c.name ? "ga-chip-active" : ""}`} data-testid={`filter-chip-${c.name}`}>{c.name} <span className="opacity-60">{c.count}</span></button>
        ))}
      </div>

      {historyDays && (
        <p className="ga-muted mt-3 text-[11px]" data-testid="memories-history-notice">Plano gratuito: mostrando os últimos {historyDays} dias. <Link to="/planos" className="font-semibold" style={{ color: "#0A6373" }}>Ver histórico completo</Link></p>
      )}

      <div className="mt-4 space-y-2.5" data-testid="memories-list">
        {items === null && [1, 2, 3, 4].map((i) => <div key={i} className="ga-card h-[74px] animate-pulse" />)}
        {items?.length === 0 && (
          <div className="ga-card p-8 text-center" data-testid="memories-empty-state">
            <p className="text-sm font-medium">{q ? "Não achei essa informação nas suas memórias." : "Nenhuma memória por aqui."}</p>
            {q && <Link to={`/perguntar?q=${encodeURIComponent(q)}`} className="mt-2 inline-block text-xs font-semibold" style={{ color: "#0A6373" }} data-testid="memories-ask-instead">Perguntar ao Clicou, Guardou!</Link>}
          </div>
        )}
        {items?.map((m) => (
          <MemoryCard key={m.id} memory={m} onFavorite={(x) => patch(x, { favorite: !x.favorite })} onArchive={(x) => patch(x, { archived: !x.archived }, x.archived ? "Memória restaurada." : "Memória arquivada.")} onDelete={remove} />
        ))}
      </div>
    </div>
  );
}
